<template>
  <div
    class="flex justify-center"
    :class="[
      {
        'h-[11px] pb-0.5 lg:h-5 lg:pb-1': fromListingPage,
        'rounded-full border-2 border-solid p-0.5': !fromListingPage,
        // 'lg:h-8 lg:pb-1': !fromListingPage,
      },
      { 'border-black-40': hasActiveDot },
      { 'border-white': !hasActiveDot },
    ]"
  >
    <span
      :key="color ? `color-picker-color-${color.value}` : 'color-picker-empty'"
      :title="color ? title || color.label : undefined"
      :data-color-id="color?.id"
      :aria-hidden="decorative || undefined"
      :style="
        color
          ? `background:${
              isMultiColor
                ? 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)'
                : displayColor
            }`
          : undefined
      "
      class="flex items-center justify-center rounded-full"
      :class="[
        { 'border border-solid border-black': color && isWhiteColor },
        { 'border border-dotted': color && isNoColor },
        // Unknown/empty colors keep the size but render as an outlined,
        // background-less placeholder instead of collapsing the swatch.
        { 'border border-solid border-black-40': !color },
        {
          'size-2 lg:size-[15px]': fromListingPage,
          'size-5 lg:size-[24px]': !fromListingPage,
        },
      ]"
    />
  </div>
</template>

<script setup lang="ts">
import type { ProductColor } from '@scayle/storefront-nuxt'

const props = defineProps({
  color: {
    type: Object as PropType<ProductColor>,
    default: () => {},
  },
  hasActiveDot: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  fromListingPage: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  title: {
    type: String as PropType<string>,
    default: '',
  },
  // ECOM-2461: when true, hide the decorative swatch from assistive tech so it
  // does not pollute an ancestor link's accessible name. Opt-in (default false)
  // so consumers whose only accessible name is the swatch title stay unaffected.
  decorative: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const displayColor = computed(() => {
  if (!props.color) {
    return '#ffffff'
  }

  if (Number(props.color.value) === props.color.id) {
    const color = getColorCodeForId(props.color.id)
    if (color !== '#ffffff') {
      return color
    }
  }

  const pattern = /^#(?:[0-9a-fA-F]{3}){1,2}$/
  if (pattern.test(`#${props.color.value}`)) {
    return `#${props.color.value}`
  }

  return '#ffffff'
})

const isWhiteColor = computed(
  () => displayColor.value.toUpperCase() === '#FFFFFF',
)
const isNoColor = computed(
  () => getColorCodeForId(props.color.id) === 'no-color',
)
const isMultiColor = computed(
  () => getColorCodeForId(props.color.id) === 'multi-color',
)
</script>
